import { PropsWithChildren } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";

type NotificationBannerVariant = "success" | "warning" | "error";

const VARIANTS = {
  success: {
    className: "bg-lightGreen",
    Icon: CheckCircleIcon,
    iconClassName: "text-success",
    role: "status",
    ariaLive: "polite",
  },
  warning: {
    className: "bg-lightYellow",
    Icon: ExclamationCircleIcon,
    iconClassName: "text-yellow",
    role: "status",
    ariaLive: "polite",
  },
  error: {
    className: "bg-red-50",
    Icon: ExclamationCircleIcon,
    iconClassName: "text-red-600",
    role: "alert",
    ariaLive: "assertive",
  },
} as const;

type NotificationBannerProps = PropsWithChildren<{
  variant: NotificationBannerVariant;
  title?: string;
  onDismiss?: () => void;
}>;

export const NotificationBanner = ({
  variant,
  title,
  onDismiss,
  children,
}: NotificationBannerProps) => {
  const { className, Icon, iconClassName, role, ariaLive } = VARIANTS[variant];

  const dismissButton = onDismiss && (
    <button
      type="button"
      aria-label="Dismiss"
      className="text-indigo shrink-0 rounded-full p-0.5 transition-opacity hover:opacity-70"
      onClick={onDismiss}
    >
      <XMarkIcon className="size-5" />
    </button>
  );

  if (title) {
    return (
      <div
        role={role}
        aria-live={ariaLive}
        className={`flex w-full items-start gap-3 rounded-sm p-4 text-sm ${className}`}
      >
        <Icon
          className={`mt-0.5 size-5 shrink-0 ${iconClassName}`}
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-indigo font-medium">{title}</p>
          <div className="text-dark-blue-gray">{children}</div>
        </div>
        {dismissButton}
      </div>
    );
  }

  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={`text-indigo flex w-full items-center justify-center gap-2 px-4 py-3 text-sm ${className}`}
    >
      <Icon
        className={`h-6 w-6 shrink-0 ${iconClassName}`}
        aria-hidden="true"
      />
      <span>{children}</span>
      {dismissButton}
    </div>
  );
};
